import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import { DiskInfo, FileItem, FolderItem, uploadFileForm } from '../types/files'
import loadServerDiscInfo from '../api/loadServerDiscInfo'
import loadFiles from '../api/loadFiles'
import loadFolders from '../api/loadFolders'
import loadAllFolders from '../api/loadAllFolders'
import createFolderApi, { CreateFolderPayload } from '../api/createFolder'
import updateFolderApi from '../api/updateFolder'
import { uploadFile as uploadFileApi } from '../api/uploadFile'
import removeFile from '../api/removeFile'
import removeFolder from '../api/removeFolder'
import updateFile from '../api/updateFile'

// get all files
export const getFiles = createAsyncThunk(
  'file/getFiles',
  async (folderId: string | undefined, thunkApi) => {
    try {
      return await loadFiles(folderId)
    } catch (e) {
      return thunkApi.rejectWithValue('filesError')
    }
  },
)

export const getFolders = createAsyncThunk(
  'file/getFolders',
  async (parentId: string | undefined, thunkApi) => {
    try {
      return await loadFolders(parentId)
    } catch (e) {
      return thunkApi.rejectWithValue('foldersError')
    }
  },
)

export const createFolder = createAsyncThunk(
  'file/createFolder',
  async (payload: CreateFolderPayload, thunkApi) => {
    try {
      return await createFolderApi(payload)
    } catch (e) {
      return thunkApi.rejectWithValue('folderCreateError')
    }
  },
)

export const getAllFolders = createAsyncThunk(
  'file/getAllFolders',
  async (_, thunkApi) => {
    try {
      return await loadAllFolders()
    } catch (e) {
      return thunkApi.rejectWithValue('allFoldersError')
    }
  },
)

export const updateFolderAction = createAsyncThunk(
  'file/updateFolder',
  async (data: { id: string; updates: Partial<FolderItem> }, thunkApi) => {
    try {
      return await updateFolderApi(data.id, data.updates)
    } catch (e) {
      return thunkApi.rejectWithValue('folderUpdateError')
    }
  },
)

export const deleteFolderAction = createAsyncThunk(
  'file/deleteFolder',
  async (id: string, thunkApi) => {
    try {
      return await removeFolder(id)
    } catch (e) {
      return thunkApi.rejectWithValue('folderDeleteError')
    }
  },
)

//download file
export const downloadFile = createAsyncThunk(
  'file/downloadFile',
  async (fileId: string, thunkApi) => {
    try {
      // return await loadFile(fileId)
    } catch (e) {
      return thunkApi.rejectWithValue('downloadFileError')
    }
  },
)

// upload file
export const uploadFile = createAsyncThunk(
  'file/uploadFile',
  async (file: uploadFileForm, thunkApi) => {
    try {
      return await uploadFileApi(file)
    } catch (e) {
      return thunkApi.rejectWithValue('filUploadError')
    }
  },
)

// delete file
export const deleteFile = createAsyncThunk(
  'file/deleteFile',
  async (id: string, thunkApi) => {
    try {
      return await removeFile(id)
    } catch (e) {
      return thunkApi.rejectWithValue('fileError')
    }
  },
)

// update file
export const updateFileAction = createAsyncThunk(
  'file/updateFile',
  async (data: { id: string; updates: Partial<FileItem> }, thunkApi) => {
    try {
      return await updateFile(data.id, data.updates)
    } catch (e) {
      return thunkApi.rejectWithValue('fileUpdateError')
    }
  },
)

// get server disk info
export const getSeverDiscInfo = createAsyncThunk(
  'file/getSeverDiscInfo',
  async (_, thunkApi) => {
    try {
      return await loadServerDiscInfo()
    } catch (e) {
      return thunkApi.rejectWithValue('fileError')
    }
  },
)

interface FileSliceState {
  files: FileItem[]
  folders: FolderItem[]
  allFolders: FolderItem[]
  activeFilesFolderId?: string
  activeFoldersParentId?: string
  filePreview?: FileItem
  serverDiscsInfo: DiskInfo[]
  fileLoading: boolean
  fileError?: SerializedError
}

const initialState: FileSliceState = {
  files: [],
  folders: [],
  allFolders: [],
  activeFilesFolderId: undefined,
  activeFoldersParentId: undefined,
  serverDiscsInfo: [],
  filePreview: undefined,
  fileLoading: false,
  fileError: undefined,
}

const fileSlice = createSlice({
  name: 'file',
  initialState,
  reducers: {
    // setFiles: (state, action: PayloadAction<File[]>) => {
    //   //@ts-ignore
    //   state.files = action.payload
    // },
    // setFile: (state, action: PayloadAction<File | undefined>) => {
    //   //@ts-ignore
    //   state.file = action.payload
    // },
    // setFileDetails: (state, action: PayloadAction<File | undefined>) => {
    //   //@ts-ignore
    //   state.fileDetails = action.payload
    // },
  },
  extraReducers: (builder) => {
    builder.addCase(getFiles.fulfilled, (state, action: any) => {
      if (state.activeFilesFolderId !== action.meta.arg) {
        return
      }

      state.files = action.payload
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(getFiles.pending, (state, action: any) => {
      state.activeFilesFolderId = action.meta.arg
      state.files = []
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(getFiles.rejected, (state, action: any) => {
      if (state.activeFilesFolderId !== action.meta.arg) {
        return
      }

      state.files = []
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(getFolders.fulfilled, (state, action: any) => {
      if (state.activeFoldersParentId !== action.meta.arg) {
        return
      }

      state.folders = action.payload
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(getFolders.pending, (state, action: any) => {
      state.activeFoldersParentId = action.meta.arg
      state.folders = []
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(getFolders.rejected, (state, action: any) => {
      if (state.activeFoldersParentId !== action.meta.arg) {
        return
      }

      state.folders = []
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(createFolder.fulfilled, (
      state,
      action: PayloadAction<FolderItem>,
    ) => {
      state.folders = [...state.folders, action.payload]
      state.allFolders = [...state.allFolders, action.payload]
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(createFolder.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(createFolder.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(updateFolderAction.fulfilled, (
      state,
      action: PayloadAction<FolderItem>,
    ) => {
      const updatedFolder = action.payload
      state.folders = state.folders
        .filter((folder) => folder.id !== updatedFolder.id)
        .concat(
          state.activeFoldersParentId === (updatedFolder.parentId || undefined)
            ? [updatedFolder]
            : [],
        )
      state.allFolders = state.allFolders.map((folder) =>
        folder.id === updatedFolder.id ? updatedFolder : folder,
      )
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(updateFolderAction.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(updateFolderAction.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(getAllFolders.fulfilled, (
      state,
      action: PayloadAction<FolderItem[]>,
    ) => {
      state.allFolders = action.payload || []
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(getAllFolders.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(getAllFolders.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(deleteFolderAction.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.folders = state.folders.filter(
        (folder) => folder.id !== action.payload,
      )
      state.allFolders = state.allFolders.filter(
        (folder) => folder.id !== action.payload,
      )
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(deleteFolderAction.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(deleteFolderAction.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    // NOTE: we intentionally do NOT mutate state.files on upload success.
    // The page refetches getFiles(currentFolderId) after a successful upload.
    builder.addCase(uploadFile.fulfilled, (state) => {
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(uploadFile.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(uploadFile.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(deleteFile.fulfilled, (state, action: PayloadAction<string>) => {
      state.files = state.files.filter((file) => file.id !== action.payload)
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(deleteFile.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(deleteFile.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(updateFileAction.fulfilled, (
      state,
      action: PayloadAction<FileItem>,
    ) => {
      state.files = state.files.map((file) =>
        file.id === action.payload.id ? action.payload : file,
      )
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(updateFileAction.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(updateFileAction.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
    builder.addCase(getSeverDiscInfo.fulfilled, (
      state,
      action: PayloadAction<DiskInfo[]>,
    ) => {
      state.serverDiscsInfo = action.payload || []
      state.fileLoading = false
      state.fileError = undefined
    })
    builder.addCase(getSeverDiscInfo.pending, (state) => {
      state.fileLoading = true
      state.fileError = undefined
    })
    builder.addCase(getSeverDiscInfo.rejected, (state, action) => {
      state.fileLoading = false
      state.fileError = action.error
    })
  },

  // (builder) => {
  //     builder.addCase(getDevelopers.fulfilled, (state, action) => {
  //         state.developers = action.payload as IDeveloper[];
  //     })

  //     builder.addCase(addDeveloper.fulfilled, (state, action) => {
  //         state.developers.push(action.payload as IDeveloper);
  //     })

  //     builder.addCase(deleteDeveloper.fulfilled, (state, action) => {
  //         state.developers = state.developers.filter(developer => developer.id != action.payload)
  //     })
  // }
})

export default fileSlice.reducer
